/** Klient verejneho API vylohy. Bez cookies, bez prihlaseni. */
import type { Storefront } from './storefront/types';
import type { LayerDeliveryResponse, LayerStatKind } from './layers/types';

/** Zmensena varianta obrazku. */
export interface TrackedStep {
  readonly status: string;
  readonly label: string;
  readonly tone: string;
  readonly at: string | null;
  readonly done: boolean;
  readonly current: boolean;
}

export interface TrackedOrder {
  readonly orderNumber: string;
  readonly placedAt: string;
  readonly status: string;
  readonly statusLabel: string;
  readonly totalMinor: number;
  readonly currency: string;
  readonly paymentLabel: string;
  readonly paidAt: string | null;
  readonly shippingLabel: string;
  readonly pickupPointName: string | null;
  readonly trackingUrl: string | null;
  readonly hasInvoice: boolean;
  readonly items: readonly {
    name: string;
    variantName: string;
    quantity: number;
    lineTotalMinor: number;
  }[];
  readonly steps: readonly TrackedStep[];
}

export interface Thumbnail {
  readonly width: number;
  readonly url: string;
}

/**
 * Sestavi `srcset`. Prohlizec si pak vezme tu velikost, kterou opravdu
 * potrebuje – dlazdice v katalogu nema stahovat fotku z fotoaparatu.
 *
 * U obrazku bez variant (nahrane driv, nez se nahledy zavedly) vraci
 * `undefined`, takze se pouzije obycejny `src`.
 */
export function srcSet(thumbnails: readonly Thumbnail[]): string | undefined {
  // Data z localStorage (oblíbené, naposledy prohlížené) může upravit kdokoli.
  // Přeskočíme rozbité prvky, ať render dlaždice nespadne na chybějícím poli.
  const valid = thumbnails.filter(
    (t) => t != null && typeof t.url === 'string' && typeof t.width === 'number',
  );
  if (valid.length === 0) return undefined;
  return valid.map((thumbnail) => `${thumbnail.url} ${thumbnail.width}w`).join(', ');
}

export class ShopError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly field: string | null = null,
  ) {
    super(message);
    this.name = 'ShopError';
  }
}

async function request<T>(path: string, options: { method?: string; body?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { 'x-requested-with': 'zooshopik-admin' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      // Zákaznická session je v httpOnly cookie. Výloha běží za stejnou proxy
      // jako API (same-origin), ale `include` drží přihlášení i kdyby API
      // jelo na jiném původu v dev režimu. Na veřejné GETy bez cookie to vliv
      // nemá – server je ignoruje.
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new ShopError(0, 'network_error', 'Nepodařilo se spojit se serverem.');
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const body = (parsed ?? {}) as { error?: string; message?: string; field?: string };
    throw new ShopError(
      response.status,
      body.error ?? 'unknown_error',
      body.message ?? 'Něco se pokazilo.',
      body.field ?? null,
    );
  }

  return parsed as T;
}

export interface ShopVariant {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly priceMinor: number;
  readonly compareAtPriceMinor: number | null;
  readonly available: number;
  readonly weightGrams: number | null;
}

export interface ShopProduct {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly description: string;
  readonly brand: string | null;
  readonly vatRate: number;
  readonly variants: readonly ShopVariant[];
  readonly categories: readonly { slug: string; name: string }[];
  readonly images: readonly { url: string; alt: string; thumbnails: readonly Thumbnail[] }[];
  readonly ratingAverage: number;
  readonly ratingCount: number;
  readonly createdAt: string;
}

export interface Review {
  readonly id: string;
  readonly rating: number;
  readonly authorName: string;
  readonly body: string;
  readonly createdAt: string;
}

export interface ContentPage {
  readonly slug: string;
  readonly title: string;
  readonly body: string;
  readonly updatedAt: string;
}

/** Slovník pojmů – položka rejstříku A–Z. */
export interface GlossaryIndexEntry {
  readonly slug: string;
  readonly term: string;
  readonly excerpt: string;
  readonly aliases: readonly string[];
}

/** Produkt na detailu hesla – dlaždice „Kde to koupíš". */
export interface GlossaryProductTile {
  readonly slug: string;
  readonly name: string;
  readonly imageUrl: string | null;
  readonly priceFromMinor: number | null;
  readonly inStock: boolean;
}

/** Slovník pojmů – detail hesla. */
export interface GlossaryTerm {
  readonly slug: string;
  readonly term: string;
  readonly aliases: readonly string[];
  readonly excerpt: string;
  readonly body: string;
  readonly updatedAt: string;
  readonly products: readonly GlossaryProductTile[];
  readonly categories: readonly { slug: string; name: string }[];
}

export interface ShopListItem {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly brand: string | null;
  readonly priceFromMinor: number;
  readonly priceToMinor: number;
  readonly compareAtPriceFromMinor: number | null;
  readonly inStock: boolean;
  readonly imageUrl: string | null;
  readonly imageThumbnails: readonly Thumbnail[];
  /**
   * Kolik aktivnich variant produkt ma, a ta jedina, kdyz je sam. Dlazdice
   * podle toho nabidne kosik, nebo posle na detail vybrat variantu.
   */
  readonly variantCount: number;
  readonly singleVariantId: string | null;
  readonly singleVariantName: string | null;
  readonly singleVariantAvailable: number;
  readonly ratingAverage: number;
  readonly ratingCount: number;
  /** Zalozeni produktu – stitek „Novinka". */
  readonly createdAt: string;
}

/** Klice razeni katalogu. Musi sedet s vyctem v `/api/shop/products`. */
export type SortKey = 'relevance' | 'price_asc' | 'price_desc' | 'newest' | 'rating' | 'name';

export interface ShippingOffer {
  readonly code: string;
  readonly name: string;
  readonly description: string;
  readonly basePriceMinor: number;
  readonly priceMinor: number;
  readonly isFree: boolean;
  readonly missingToFreeMinor: number | null;
  readonly requiresPickupPoint: boolean;
  readonly allowsCashOnDelivery: boolean;
  /** 'internal' = náš seznam z importu, 'ppl_widget' = oficiální widget PPL. */
  readonly pickupSource: 'internal' | 'ppl_widget';
  readonly widgetApiKey: string;
}

/** Bod tak, jak ho vrátí widget dopravce. */
export interface WidgetPoint {
  readonly code: string;
  readonly name: string;
  readonly street: string;
  readonly city: string;
  readonly postalCode: string;
  readonly countryCode: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly accessPointType: string | null;
  readonly cardPayment: boolean | null;
  readonly cashPayment: boolean | null;
}

export interface PaymentOption {
  readonly code: string;
  readonly name: string;
  readonly description: string;
  readonly feeMinor: number;
  readonly isCashOnDelivery: boolean;
}

export interface DeliveryOptions {
  readonly cart: {
    readonly itemsTotalMinor: number;
    readonly weightGrams: number;
    readonly hasUnknownWeight: boolean;
  };
  readonly shipping: readonly ShippingOffer[];
  readonly payment: readonly PaymentOption[];
}

export interface PickupPoint {
  readonly code: string;
  readonly carrier: string;
  readonly name: string;
  readonly street: string;
  readonly city: string;
  readonly postalCode: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly note: string;
  readonly distanceKm?: number;
}

export interface AddressInput {
  fullName: string;
  company?: string | null;
  companyId?: string | null;
  vatId?: string | null;
  street: string;
  city: string;
  postalCode: string;
  /** ISO kód země doručení (CZ výchozí). Řídí sazbu DPH (režim OSS). */
  countryCode?: string;
}

export interface CouponEffect {
  readonly couponId: string;
  readonly code: string;
  readonly kind: 'percent' | 'fixed' | 'free_shipping';
  readonly discountMinor: number;
  readonly freeShipping: boolean;
}

/**
 * Darkovy poukaz uplatneny na TENHLE kosik.
 *
 * `appliedMinor` je cast, ktera na kosik skutecne sedne – poukaz kryje jen
 * ZBOZI po akcich a po kuponu, ne dopravu ani priplatek za platbu. Presne
 * tahle castka patri do souhrnu (a odchazi jako `expectedVoucherMinor`).
 *
 * ZUSTATEK POUKAZU SERVER NEPOSILA a vyloha ho nepotrebuje: vykresluje se
 * jen uplatnena castka. Kdyby zustatek chodil ven, prozradil by kazdy
 * uniknuty kod i to, kolik na nem jeste zbyva.
 */
export interface VoucherEffect {
  readonly code: string;
  readonly appliedMinor: number;
}

/**
 * Stitek akce pro dlazdici/detail. Text `badge` i `name` chodi ze serveru
 * (uz lokalizovany do CZ, jako u kuponu) – vyloha nic nepocita ani nesklada.
 */
export interface PromotionLabel {
  readonly name: string;
  readonly badge: string;
}

/** Jedna uplatnena akce v nahledu kosiku. Vsechny castky ze serveru. */
export interface CartPromotion {
  readonly name: string;
  readonly rewardKind: string;
  readonly discountMinor: number;
  readonly applications: number;
}

/** Darek, ktery akce pridava. Radek objednavky za plnou cenu, sleva = cela cena. */
export interface CartGift {
  readonly variantId: string;
  readonly productName: string;
  readonly variantName: string;
  readonly sku: string;
  readonly quantity: number;
  readonly unitPriceMinor: number;
}

/** Odpoved `/api/shop/promotions` – nahled akci nad kosikem. */
export interface PromotionsPreview {
  readonly promotions: readonly CartPromotion[];
  readonly gifts: readonly CartGift[];
  /** Kolik zákazník celkem ušetřil – VČETNĚ ceny dárku. Jen pro sdělení. */
  readonly promoDiscountMinor: number;
  /** Hodnota zboží před akcemi, už po množstevních slevách. */
  readonly itemsTotalMinor: number | null;
  /**
   * ZÁVAZNÁ hodnota zboží: po množstevních slevách a po akcích. Přesně tohle
   * pokladna za zboží účtuje a přesně z tohohle čísla se skládá souhrn –
   * výloha si ho nesmí počítat sama. `null` = žádná akce není nakonfigurovaná,
   * platí souhrn z `delivery-options`.
   */
  readonly goodsTotalMinor: number | null;
}

/** Akce uplatnena na hotove objednavce – snapshot ze chvile objednani. */
export interface OrderPromotion {
  readonly name: string;
  readonly rewardKind: string;
  readonly discountMinor: number;
  /** Sazba DPH, kdyz sleva padla do jedine sazby; jinak `null`. */
  readonly vatRate: number | null;
  readonly applications: number;
}

export interface PlacedOrder {
  readonly orderNumber: string;
  readonly email: string;
  readonly totalMinor: number;
  readonly discountMinor: number;
  readonly couponCode: string | null;
  readonly currency: string;
  /** Kurz (jednotek `currency` za 1 Kč), kterym byla objednavka prezentovana. */
  readonly exchangeRate: number;
  readonly paymentMethod: string | null;
  readonly status: string;
  /**
   * Podil automatickych akci a jejich seznam. Aditivni pole – bez akci prijde
   * ze serveru 0 a prazdne pole, takze jsou VOLITELNA.
   */
  readonly promoDiscountMinor?: number;
  readonly promotions?: readonly OrderPromotion[];
}

/** Přihlášený zákazník výlohy. */
export interface Customer {
  readonly id: string;
  readonly email: string;
  readonly fullName: string | null;
  readonly phone: string | null;
}

/** Souhrn objednávky pro historii v účtu. Detail dotáhne sledování. */
export interface CustomerOrderSummary {
  readonly orderNumber: string;
  readonly placedAt: string;
  readonly status: string;
  readonly statusLabel: string;
  readonly tone: string;
  readonly totalMinor: number;
  readonly currency: string;
  readonly itemCount: number;
}

/** Uvítací popup se slevou – config z administrace. */
export interface SitePopup {
  readonly enabled: boolean;
  readonly heading: string;
  readonly body: string;
  readonly discountCode: string;
  readonly ctaLabel: string;
  readonly imageUrl: string;
  readonly delaySeconds: number;
}

/** Rozcestnik kategorie: kde jsem (`ancestors`) a kam dal (`children`). */
export interface CategoryNavigation {
  readonly category: { readonly slug: string; readonly name: string };
  readonly ancestors: readonly { slug: string; name: string }[];
  readonly children: readonly { slug: string; name: string; productCount: number }[];
}

/**
 * VĚRNOSTNÍ BODY (#40) — ZŮSTATEK ÚČTU.
 *
 * Čte se jen pro PŘIHLÁŠENÉHO zákazníka (`GET /api/shop/loyalty`, jinak 401):
 * body jsou peníze a identita se bere ze session, nikdy z požadavku.
 *
 * `pointValueMinor` chodí ze serveru, aby kurz nebyl ve výloze zadrátovaný —
 * až se změní, změní se na jednom místě (`pricing/loyalty.ts`).
 */
export interface LoyaltyBalance {
  readonly balancePoints: number;
  readonly pointValueMinor: number;
}

/**
 * KOLIK BODŮ SEDNE NA TENHLE KOŠÍK (`POST /api/shop/loyalty/preview`).
 *
 * Zůstatek sám o sobě nestačí: body kryjí jen ZBOŽÍ po akcích a PO KUPÓNU,
 * ne dopravu ani příplatek za platbu, a uplatnění se zaokrouhluje dolů na
 * celý bod. `maxPoints` je výsledek TÉŽE funkce, jakou počítá pokladna
 * (`splitCartDiscounts`) — víc výloha nabídnout nesmí, jinak by pokladna
 * uplatnila míň, součet by nesedl a objednávka spadla na `expectedTotalMinor`.
 *
 * `redeemableBaseMinor` je zbytek základu PO KUPÓNU. Slouží k jedinému:
 * poukaz se dělí AŽ ZA body, takže s uplatněnými body se jeho uplatněná
 * částka zmenšuje — a `POST /api/shop/voucher` o bodech neví.
 *
 * Počet bodů se do náhledu ZÁMĚRNĚ neposílá: odpověď na něm nezávisí, takže
 * hraní s políčkem nevyrábí další dotazy (a s kupónem v košíku by ubíraly
 * z brzdy na hádání kódů).
 */
export interface LoyaltyPreview extends LoyaltyBalance {
  readonly maxPoints: number;
  readonly redeemableBaseMinor: number;
}

export const api = {
  listProducts: (
    query: {
      search?: string;
      category?: string;
      sort?: SortKey;
      inStock?: boolean;
      brand?: string;
      /** Parametricke filtry, `prichut:kureci,rybi;vek:stene`. Viz `storefront/facets.ts`. */
      parametry?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (query.search !== undefined && query.search !== '') params.set('search', query.search);
    if (query.category !== undefined && query.category !== '') params.set('category', query.category);
    if (query.sort !== undefined) params.set('sort', query.sort);
    if (query.inStock === true) params.set('inStock', 'true');
    if (query.brand !== undefined && query.brand !== '') params.set('brand', query.brand);
    if (query.parametry !== undefined && query.parametry !== '') {
      params.set('parametry', query.parametry);
    }
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.offset !== undefined && query.offset > 0) {
      params.set('offset', String(query.offset));
    }
    const search = params.toString();
    return request<{ items: ShopListItem[]; total: number }>(
      `/api/shop/products${search === '' ? '' : `?${search}`}`,
    );
  },

  /** Vzhled vylohy – motiv a bloky domovske stranky nastavene v administraci. */
  getStorefront: () => request<Storefront>('/api/shop/storefront'),

  getProduct: (slug: string) =>
    request<{
      product: ShopProduct;
      reviews: Review[];
      related: ShopListItem[];
      volumeDiscounts: { minQuantity: number; percent: number }[];
    }>(`/api/shop/products/${slug}`),

  submitReview: (
    slug: string,
    body: { rating: number; authorName?: string; authorEmail?: string; body?: string },
  ) =>
    request<{ ok: boolean; pending: boolean }>(`/api/shop/products/${slug}/reviews`, {
      method: 'POST',
      body,
    }),

  watchAvailability: (
    slug: string,
    body: { variantId: string; email: string; kind?: 'availability' | 'price' },
  ) =>
    request<{ ok: boolean; duplicate: boolean }>(`/api/shop/products/${slug}/watch`, {
      method: 'POST',
      body,
    }),

  /**
   * `visitorId` (CDP) se posílá jen se souhlasem s analytikou. `source`
   * (whitelist footer|popup) a `layerId` slouží k atribuci konverze u vrstev
   * Fáze 7 — jediná zápisová cesta odběratele zůstává tahle.
   */
  subscribeNewsletter: (
    email: string,
    visitorId?: string,
    opts: { source?: 'footer' | 'popup'; layerId?: string } = {},
  ) =>
    request<{ ok: boolean; duplicate: boolean }>('/api/shop/newsletter', {
      method: 'POST',
      body: {
        email,
        ...(visitorId === undefined ? {} : { visitorId }),
        ...(opts.source === undefined ? {} : { source: opts.source }),
        ...(opts.layerId === undefined ? {} : { layerId: opts.layerId }),
      },
    }),

  sitePopup: () => request<{ popup: SitePopup }>('/api/shop/site-popup'),

  /* ------------------------------------------------ pop-upy a weblayery (F7) */

  /**
   * Doručovací endpoint vrstev. Odpověď ZÁVISÍ NA IDENTITĚ (session cookie),
   * proto POST + `credentials:'include'`. `visitor_id` se posílá JEN se
   * souhlasem s analytikou. Fail-silent řeší volající (`layers/client.ts`).
   */
  resolveLayers: (body: {
    visitor_id: string | null;
    session_id: string | null;
    consent: { analytics: boolean; marketing: boolean; version: number };
    context: { path: string; lang: string; device: 'desktop' | 'mobile' };
    preview?: { layerId: string; token: string };
  }) => request<LayerDeliveryResponse>('/api/shop/layers', { method: 'POST', body }),

  /**
   * Statistický maják vrstev (agregát vždy, per-identita jen u přihlášených).
   * Fire-and-forget, `keepalive` přežije unload — vlastní fetch mimo
   * `request<T>`, protože nikdy nesmí vyhodit ani shodit UI.
   */
  recordLayerStats: (
    events: readonly { layer_id: string; kind: LayerStatKind }[],
    keepalive = false,
  ): void => {
    if (events.length === 0) return;
    try {
      void fetch('/api/shop/layers/stat', {
        method: 'POST',
        keepalive,
        credentials: 'include',
        headers: { 'content-type': 'application/json', 'x-requested-with': 'zooshopik-admin' },
        body: JSON.stringify({ events }),
      }).catch(() => {
        // Statistika nesmí nikdy shodit výlohu.
      });
    } catch {
      // Ani serializace/keepalive limit.
    }
  },

  /**
   * Ne-e-mailová pole formuláře vrstvy → `cdp_customer_attribute`. Volá se AŽ
   * po úspěšném `subscribeNewsletter` (e-mail nikdy touto cestou). Server
   * přijme jen atributy, které vrstva deklaruje.
   */
  submitLayerForm: (layerId: string, email: string, fields: Record<string, unknown>) =>
    request<{ ok: boolean }>('/api/shop/layers/submit', {
      method: 'POST',
      body: { layerId, email, fields },
    }),

  /** Strom kategorii pro navigacni pruh – top-level i s podkategoriemi. */
  listCategoryTree: () =>
    request<{
      categories: { slug: string; name: string; children: { slug: string; name: string }[] }[];
    }>('/api/shop/category-tree'),

  /**
   * Rozcestnik jedne kategorie – drobeckova cesta a prime podkategorie.
   *
   * Pocet u podkategorie je za celou jeji vetev, takze sedi s tim, co po
   * prokliku ukaze mrizka (`?kategorie=` bere kategorii vcetne potomku).
   * 404 = neznamy slug; volajici z toho udela „zadny rozcestnik".
   */
  categoryNavigation: (slug: string) =>
    request<CategoryNavigation>(`/api/shop/categories/${encodeURIComponent(slug)}`),

  listBrands: () => request<{ brands: string[] }>('/api/shop/brands'),

  bestsellers: () => request<{ items: ShopListItem[] }>('/api/shop/bestsellers'),

  newest: (limit = 8) =>
    request<{ items: ShopListItem[] }>(`/api/shop/newest?limit=${encodeURIComponent(String(limit))}`),

  /** Kurzy cizích měn (jednotek za 1 Kč) – z ČNB, s fallbackem na serveru. */
  rates: () =>
    request<{
      base: 'CZK';
      asOf: string | null;
      source: 'cnb' | 'fallback';
      rates: Record<string, number>;
    }>('/api/shop/rates'),

  specials: () => request<{ items: ShopListItem[] }>('/api/shop/specials'),

  submitContact: (body: { name: string; email: string; subject?: string; body: string }) =>
    request<{ ok: boolean }>('/api/shop/contact', { method: 'POST', body }),

  listPages: () => request<{ pages: { slug: string; title: string }[] }>('/api/shop/pages'),

  /**
   * Kam vede stara adresa po prejmenovani. `null` = zadne pravidlo neexistuje.
   *
   * Spravna odpoved je 301 ze serveru a API ji umi (`GET /produkt/:slug`),
   * jenze nginx dnes pousti na API jen /api, /uploads, /feed a sitemapu –
   * na detail produktu se tedy nedostane. Dokud to tak je, doptava se vyloha
   * sama a presmeruje v prohlizeci. Neni to plnohodnotna nahrada (robot
   * stavovy kod nedostane), ale navstevnik ze stareho odkazu skonci na zbozi
   * misto na hlasce, ze stranka neexistuje.
   */
  redirectFor: async (path: string): Promise<string | null> => {
    try {
      const result = await request<{ target: string }>(
        `/api/seo/redirect?path=${encodeURIComponent(path)}`,
      );
      return result.target;
    } catch {
      // 404 i vypadek site znamenaji totez: nemame kam poslat.
      return null;
    }
  },

  getPage: (slug: string) => request<{ page: ContentPage }>(`/api/shop/pages/${slug}`),

  listGlossary: () => request<{ terms: GlossaryIndexEntry[] }>('/api/shop/glossary'),

  getGlossaryTerm: (slug: string) =>
    request<{ term: GlossaryTerm }>(`/api/shop/glossary/${encodeURIComponent(slug)}`),

  deliveryOptions: (items: readonly { variantId: string; quantity: number }[], shippingMethod?: string) =>
    request<DeliveryOptions>('/api/shop/delivery-options', {
      method: 'POST',
      body: { items, ...(shippingMethod === undefined ? {} : { shippingMethod }) },
    }),

  pickupPoints: (carrier: string, options: { query?: string; lat?: number; lng?: number } = {}) => {
    const params = new URLSearchParams({ carrier });
    if (options.query !== undefined && options.query !== '') params.set('query', options.query);
    // Souradnice davaji smysl jen obe najednou.
    if (options.lat !== undefined && options.lng !== undefined) {
      params.set('lat', String(options.lat));
      params.set('lng', String(options.lng));
    }
    return request<{ points: PickupPoint[] }>(`/api/shop/pickup-points?${params.toString()}`);
  },

  /**
   * Nahled AUTOMATICKYCH AKCI nad kosikem. Telo nese JEN obsah kosiku –
   * zadne castky, zadna id akci. Vsechno spocita a vrati server; vyloha jen
   * zobrazuje. Volajici resi fail-silent (vypadek nesmi rozbit kosik).
   */
  fetchPromotions: (items: readonly { variantId: string; quantity: number }[]) =>
    request<PromotionsPreview>('/api/shop/promotions', { method: 'POST', body: { items } }),

  /**
   * Stitky akci pro dlazdice/detail. Zvlast od katalogu – vypadek se jen
   * nevykresli, nikdy nezdrzi ani neshodi vypis produktu. Max 60 id na dotaz.
   */
  fetchPromotionLabels: (productIds: readonly string[]) =>
    request<{ labels: Record<string, PromotionLabel[]> }>('/api/shop/promotions/labels', {
      method: 'POST',
      body: { productIds },
    }),

  applyCoupon: (
    items: readonly { variantId: string; quantity: number }[],
    code: string,
    email?: string,
  ) =>
    request<{ coupon: CouponEffect }>('/api/shop/coupon', {
      method: 'POST',
      body: { items, code, ...(email === undefined || email === '' ? {} : { email }) },
    }),

  /**
   * Nahled darkoveho poukazu nad kosikem. `couponCode` je nutny, protoze
   * kupon se uplatnuje PRED poukazem – bez nej by server spocital vetsi
   * uplatnenou castku, nez pokladna uzna.
   */
  applyVoucher: (
    items: readonly { variantId: string; quantity: number }[],
    code: string,
    couponCode?: string,
  ) =>
    request<{ voucher: VoucherEffect }>('/api/shop/voucher', {
      method: 'POST',
      body: {
        items,
        code,
        ...(couponCode === undefined || couponCode === '' ? {} : { couponCode }),
      },
    }),

  checkout: (body: {
    email: string;
    phone?: string | null;
    items: readonly { variantId: string; quantity: number }[];
    shippingMethod: string;
    paymentMethod: string;
    pickupPointId?: string | null;
    pickupPointName?: string | null;
    pickupPoint?: WidgetPoint | null;
    billingAddress: AddressInput;
    note?: string;
    couponCode?: string | null;
    voucherCode?: string | null;
    termsAccepted: boolean;
    /**
     * Castka, kterou vyloha ukazala v radku „Celkem" (halere CZK). Server
     * z ni nic nepocita – jen odmitne objednavku, ktera by vysla DRAZ.
     *
     * POVINNA, stejne jako na serveru (`shop/checkout.ts`): vyloha zadny
     * soucet nevykreslit nemohla, takze ho posila VZDY. Neni volitelna ani
     * v typu – kdyby ji nekdo pri pristi zmene vypustil, ma to spadnout uz
     * pri prekladu, ne az na 400 v pokladne.
     */
    expectedTotalMinor: number;
    /**
     * Castka, kterou vyloha ukazala na radku „Dárkový poukaz" (halere CZK).
     * NULA, kdyz se radek nevykreslil – prave tehdy je pole nejdulezitejsi:
     * bez nej by pokladna smela strhnout kredit, o kterem souhrn mlcel.
     * Server z ni nic nepocita, jen odmitne vetsi cerpani.
     */
    expectedVoucherMinor?: number;
    /** Prezentacni mena (CZK|EUR|PLN). Kurz i castky resi server. */
    currency?: string | null;
    /** CDP kontext (vis_/ses_ klice) – jen se souhlasem s analytikou. */
    tracking?: { visitorId: string; sessionId: string };
    /**
     * Kolik VĚRNOSTNÍCH BODŮ (ne haléřů) má pokladna uplatnit. Posílá se jen
     * s nenulovým počtem – uplatnit body smí pouze přihlášený zákazník
     * a hostovský požadavek s body pokladna odmítne.
     *
     * Je to PREFERENCE, ne příkaz: skutečně uplatněnou částku dopočítá server
     * (ořízne ji základem a zaokrouhlí dolů na celý bod).
     */
    pointsToRedeem?: number;
    /**
     * Částka, kterou výloha ukázala na řádku „Věrnostní body" (haléře CZK).
     * NULA, když se řádek nevykreslil — právě tehdy je pole nejdůležitější.
     *
     * POVINNÁ, stejně jako `expectedTotalMinor`, a ze stejného důvodu:
     * zaškrtnout uplatnění a nevidět, kolik to dělá, nejde. Kdyby ji někdo
     * při příští změně vypustil, má to spadnout už při překladu.
     *
     * Server z ní nic nepočítá. Hlídá veličinu, kterou `expectedTotalMinor`
     * hlídat NEMŮŽE: uplatnění je `min(kolik chci, kolik zbývá ze základu)`,
     * takže když mezi náhledem a pokladnou skončí akce, ZÁKLAD VZROSTE
     * a uplatní se víc bodů — a hotovost se přitom nehne.
     */
    expectedPointsMinor: number;
  }) => request<{ order: PlacedOrder }>('/api/shop/checkout', { method: 'POST', body }),

  /**
   * Zahaji nebo zopakuje platbu kartou.
   *
   * Vraci URL platebni brany, kam se zakaznik presmeruje. Volat se to smi
   * opakovane – server vrati porad tutez transakci, dokud ji zakaznik
   * nezrusi.
   */
  startPayment: (orderNumber: string, email: string) =>
    request<{ redirectUrl: string | null }>(
      `/api/shop/orders/${encodeURIComponent(orderNumber)}/pay`,
      { method: 'POST', body: { email } },
    ),

  /** Sledovani objednavky podle cisla a e-mailu. */
  trackOrder: (orderNumber: string, email: string) =>
    request<{ order: TrackedOrder }>(
      `/api/shop/orders/${encodeURIComponent(orderNumber)}?email=${encodeURIComponent(email)}`,
    ),

  /** Odkaz na fakturu. Existuje az po zaplaceni. */
  invoiceUrl: (orderNumber: string, email: string) =>
    `/api/shop/orders/${encodeURIComponent(orderNumber)}/invoice?email=${encodeURIComponent(email)}`,

  paymentStatus: (orderNumber: string, email: string) =>
    request<{
      orderStatus: string;
      payment: { status: string; redirectUrl: string | null } | null;
    }>(
      `/api/shop/orders/${encodeURIComponent(orderNumber)}/payment?email=${encodeURIComponent(email)}`,
    ),

  /* --------------------------------------------------------- zákaznický účet */

  /** Registrace – server účet založí a rovnou přihlásí (nastaví cookie). */
  registerCustomer: (body: {
    email: string;
    password: string;
    fullName?: string;
    phone?: string;
  }) => request<{ customer: Customer }>('/api/shop/register', { method: 'POST', body }),

  /** Přihlášení zákazníka. Heslo jde jen v těle POST, nikdy do URL. */
  loginCustomer: (email: string, password: string) =>
    request<{ customer: Customer }>('/api/shop/login', {
      method: 'POST',
      body: { email, password },
    }),

  logoutCustomer: () => request<{ ok: boolean }>('/api/shop/logout', { method: 'POST' }),

  /** Aktuální zákazník, nebo null když nikdo není přihlášený. */
  me: () => request<{ customer: Customer | null }>('/api/shop/me'),

  /** Objednávky přihlášeného zákazníka (jen jeho vlastní). */
  myOrders: () => request<{ orders: CustomerOrderSummary[] }>('/api/shop/my-orders'),

  /* -------------------------------------------------- věrnostní body (#40) */

  /**
   * Zůstatek bodů přihlášeného zákazníka. Bez přihlášení 401 — anonymovi
   * server o bodech neřekne nic, takže se to ani nevolá.
   */
  loyalty: () => request<{ loyalty: LoyaltyBalance }>('/api/shop/loyalty'),

  /**
   * Kolik bodů sedne na tenhle košík. `couponCode` je nutný, protože kupón se
   * uplatňuje PŘED body — bez něj by server vrátil větší strop, než pokladna
   * uzná, zákazník by zaškrtl víc bodů, než se uplatní, a součet by přestal
   * sedět s účtovanou částkou.
   */
  loyaltyPreview: (
    items: readonly { variantId: string; quantity: number }[],
    couponCode?: string,
  ) =>
    request<{ loyalty: LoyaltyPreview }>('/api/shop/loyalty/preview', {
      method: 'POST',
      body: {
        items,
        ...(couponCode === undefined || couponCode === '' ? {} : { couponCode }),
      },
    }),
};
